import React, { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { useAddons } from 'components/Addons/useAddons';
import SubscriptionForm from 'components/Modals/SubscriptionForm';
import SubscriptionLearnMore from 'components/Modals/SubscriptionLearnMore';
import { ATC } from 'components/ATC';
import { Button } from 'ui-components/v2/Button';
import { RouteIcon } from 'components/RouteIcon';
import { PurchaseFormFieldNames as FIELD_NAMES } from 'constants/form';

import { HookFormProps, PDPProps } from '../interface';
import { CTAContainerStyled, LinkStyled, DisclaimerStyled } from './styled';

interface ProductDetailsProps {
  noStock: boolean;
  subscription: 'on' | null;
  shouldShowDualButtons: boolean;
  shouldShowRouteMessage: boolean;
}

const ProductDetails = ({
  product,
  loading,
  onSubmit,
  handleSubmit,
  setValue,
  control,
  submitError,
  noStock,
  subscription,
  shouldShowDualButtons,
  shouldShowRouteMessage,
}: ProductDetailsProps & Pick<PDPProps, 'product'> & HookFormProps) => {
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const zipCode = useWatch({ control, name: FIELD_NAMES.zipCode });

  useAddons({ subscription });

  return (
    <>
      <CTAContainerStyled>
        {!product.purchasable || noStock ? (
          <DisclaimerStyled variant="body2">
            Looks like this one got away!
          </DisclaimerStyled>
        ) : null}
        <ATC
          id="form-atc"
          control={control}
          product={product}
          loading={loading}
          onSubmit={onSubmit}
          setValue={setValue}
          handleSubmit={handleSubmit}
          submitError={submitError}
          shouldShowDualButtons={shouldShowDualButtons}
        />
        {product.subscribable ? (
          <>
            <Button
              fullWidth
              color="secondary"
              variant="contained"
              onClick={() => setSubscribeOpen(true)}
              disabled={loading || !product.purchasable || !zipCode || noStock}
            >
              Make It A Recurring Delivery
            </Button>
            <LinkStyled onClick={() => setLearnMoreOpen(true)}>
              Learn more about recurring deliveries
            </LinkStyled>
            <SubscriptionForm
              id={product.id}
              purchasable={product.purchasable}
              control={control}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              setValue={setValue}
              open={subscribeOpen}
              setOpen={setSubscribeOpen}
            />
            <SubscriptionLearnMore
              open={learnMoreOpen}
              setOpen={setLearnMoreOpen}
              setFormOpen={setSubscribeOpen}
            />
          </>
        ) : null}
        {shouldShowRouteMessage ? (
          <p className="m-0 flex items-center justify-center gap-1 text-sm text-gray-800">
            <span>Package Protection at checkout with</span>
            <RouteIcon />
            <span>Route</span>
          </p>
        ) : null}
      </CTAContainerStyled>
    </>
  );
};

export default ProductDetails;
